import React, { useEffect, useMemo, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaChevronDown, FaFilter, FaSearch } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import ModernLoader from "../components/common/ModernLoader";
import api from "../services/api";
import { getMoviePosterUrl } from "../utils/imageUtils";
import "../styles/movies-cards-simple.css";

const formatToList = (value) => {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value.map((item) => String(item || "").trim()).filter(Boolean);
  }

  return String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const isNewRelease = (movie) => {
  const rawDate = movie?.releaseDate || movie?.startDate;
  if (!rawDate) return false;

  const releaseDate = new Date(rawDate);
  if (Number.isNaN(releaseDate.getTime())) return false;

  const daysDifference =
    (Date.now() - releaseDate.getTime()) / (1000 * 60 * 60 * 24);
  return daysDifference >= 0 && daysDifference <= 21;
};

const supportsFormat = (movie, formatLabel) => {
  const formatSources = [
    ...formatToList(movie?.screenType),
    ...formatToList(movie?.format),
    ...formatToList(movie?.formats),
    ...formatToList(movie?.supportedFormats),
  ]
    .map((value) => value.toLowerCase())
    .filter(Boolean);

  return formatSources.some((source) =>
    source.includes(formatLabel.toLowerCase()),
  );
};

const languageMatch = (movie, selectedLanguage) => {
  if (selectedLanguage === "All") return true;

  const normalized = selectedLanguage.toLowerCase();
  return formatToList(movie?.language)
    .map((value) => value.toLowerCase())
    .includes(normalized);
};

const genreMatch = (movie, selectedGenre) => {
  if (selectedGenre === "All") return true;

  const normalized = selectedGenre.toLowerCase();
  return formatToList(movie?.genre)
    .map((value) => value.toLowerCase())
    .includes(normalized);
};

const Movies = () => {
  const navigate = useNavigate();
  const [movies, setMovies] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedLanguage, setSelectedLanguage] = useState("All");
  const [selectedGenre, setSelectedGenre] = useState("All");
  const [onlyNewReleases, setOnlyNewReleases] = useState(false);
  const [filter3D, setFilter3D] = useState(false);
  const [filterImax2D, setFilterImax2D] = useState(false);
  const [showGenreFilters, setShowGenreFilters] = useState(false);
  const fetchMovies = useCallback(async (isSync = false) => {
    if (!isSync) setLoading(true);

    try {
      const response = await api.get("/movies", {
        params: { isAdmin: false },
      });

      const payload = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.data?.movies)
          ? response.data.movies
          : [];

      setMovies(payload);
    } catch (error) {
      console.error("Error fetching movies:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMovies();

    // Real-time update listeners
    const handleUpdate = () => {
      console.log("🔄 Silent real-time update on Movies...");
      fetchMovies(true);
    };

    window.addEventListener("movies-updated", handleUpdate);
    window.addEventListener("shows-updated", handleUpdate);
    window.addEventListener("theaters-updated", handleUpdate);

    return () => {
      window.removeEventListener("movies-updated", handleUpdate);
      window.removeEventListener("shows-updated", handleUpdate);
      window.removeEventListener("theaters-updated", handleUpdate);
    };
  }, [fetchMovies]);

  const languageChips = useMemo(() => {
    const languageSet = new Set();

    movies.forEach((movie) => {
      formatToList(movie?.language).forEach((language) => {
        if (language) languageSet.add(language);
      });
    });

    const languages = Array.from(languageSet);
    const prioritized = ["Hindi", "English"].filter((language) =>
      languages.some(
        (existing) => existing.toLowerCase() === language.toLowerCase(),
      ),
    );

    const remaining = languages
      .filter(
        (language) =>
          !prioritized.some(
            (value) => value.toLowerCase() === language.toLowerCase(),
          ),
      )
      .sort((a, b) => a.localeCompare(b))
      .slice(0, 3);

    return ["All", ...prioritized, ...remaining];
  }, [movies]);

  const genreChips = useMemo(() => {
    const genreSet = new Set();

    movies.forEach((movie) => {
      formatToList(movie?.genre).forEach((genre) => {
        if (genre) genreSet.add(genre);
      });
    });

    return ["All", ...Array.from(genreSet).sort((a, b) => a.localeCompare(b))];
  }, [movies]);

  const filteredMovies = useMemo(() => {
    return movies
      .filter((movie) => {
        if (!languageMatch(movie, selectedLanguage)) return false;
        if (!genreMatch(movie, selectedGenre)) return false;
        if (onlyNewReleases && !isNewRelease(movie)) return false;
        if (filter3D && !supportsFormat(movie, "3D")) return false;
        if (filterImax2D && !supportsFormat(movie, "IMAX 2D")) return false;

        return true;
      })
      .sort((left, right) =>
        String(left?.title || "").localeCompare(String(right?.title || "")),
      );
  }, [
    movies,
    selectedLanguage,
    selectedGenre,
    onlyNewReleases,
    filter3D,
    filterImax2D,
  ]);

  const hasActiveFilters =
    selectedLanguage !== "All" ||
    selectedGenre !== "All" ||
    onlyNewReleases ||
    filter3D ||
    filterImax2D;

  const clearFilters = () => {
    setSelectedLanguage("All");
    setSelectedGenre("All");
    setOnlyNewReleases(false);
    setFilter3D(false);
    setFilterImax2D(false);
    setShowGenreFilters(false);
  };

  if (loading) {
    return <ModernLoader text="Loading Movies" />;
  }

  return (
    <div className="district-movies-page">

      <section className="district-movies-shell">
        <header className="district-movies-heading">
          <h1>All Movies</h1>
          <p>Discover and book tickets for the latest blockbuster releases.</p>
        </header>

        <div className="district-movies-filter-sticky">
          <div className="district-movies-filter-track">
            <button
              type="button"
              className={`district-movies-chip district-filter-trigger ${showGenreFilters ? "active" : ""}`}
              onClick={() => setShowGenreFilters((previous) => !previous)}
              aria-label="Filters"
            >
              <span className="district-chip-icon-wrap">
                <FaFilter size={13} />
              </span>
              <span>Filters</span>
              <span
                className={`district-chip-arrow ${showGenreFilters ? "open" : ""}`}
              >
                <FaChevronDown size={11} />
              </span>
            </button>

            {languageChips.map((language) => (
              <button
                type="button"
                key={language}
                className={`district-movies-chip ${selectedLanguage === language ? "active" : ""}`}
                onClick={() => setSelectedLanguage(language)}
                aria-label={language}
              >
                {language}
              </button>
            ))}

            <button
              type="button"
              className={`district-movies-chip ${onlyNewReleases ? "active" : ""}`}
              onClick={() => setOnlyNewReleases((previous) => !previous)}
              aria-label="New Releases"
            >
              New Releases
            </button>

            <button
              type="button"
              className={`district-movies-chip ${filter3D ? "active" : ""}`}
              onClick={() => setFilter3D((previous) => !previous)}
              aria-label="3D"
            >
              3D
            </button>

            <button
              type="button"
              className={`district-movies-chip ${filterImax2D ? "active" : ""}`}
              onClick={() => setFilterImax2D((previous) => !previous)}
              aria-label="IMAX 2D"
            >
              IMAX 2D
            </button>

            {hasActiveFilters ? (
              <button
                type="button"
                className="district-movies-chip district-movies-chip-clear"
                onClick={clearFilters}
                aria-label="Clear all filters"
              >
                Clear
              </button>
            ) : null}
          </div>

          {showGenreFilters ? (
            <div className="district-movies-filter-track district-movies-genre-track">
              {genreChips.map((genre) => (
                <button
                  type="button"
                  key={genre}
                  className={`district-movies-chip ${selectedGenre === genre ? "active" : ""}`}
                  onClick={() => setSelectedGenre(genre)}
                  aria-label={genre}
                >
                  {genre}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <p className="district-movies-count">
          Showing {filteredMovies.length} of {movies.length} movies
        </p>

        <AnimatePresence mode="wait">
          {filteredMovies.length ? (
            <motion.div
              key="movies-grid"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.25 }}
            >
              <div className="movies-grid-container">
                {filteredMovies.map((movie, index) => {
                  const languages = formatToList(movie?.language).join(", ");
                  const languageLabel =
                    languages.length > 24
                      ? `${languages.slice(0, 24)}...`
                      : languages;

                  return (
                    <motion.div
                      key={movie._id}
                      className="movie-grid-item"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.015, duration: 0.25 }}
                    >
                      <button
                        type="button"
                        onClick={() => navigate(`/movie/${movie._id}`)}
                        className="movie-grid-link"
                        aria-label={`Open ${movie.title}`}
                      >
                        <article className="movie-grid-card">
                          <img
                            src={getMoviePosterUrl(movie.poster, movie.title)}
                            alt={movie.title}
                            loading="lazy"
                            className="movie-grid-poster"
                            onError={(event) => {
                              event.target.src = `https://via.placeholder.com/280x420/cccccc/111111?text=${encodeURIComponent(
                                movie.title,
                              )}`;
                            }}
                          />

                          <div className="movie-grid-info">
                            <h5 className="movie-grid-title">{movie.title}</h5>
                            <span className="movie-grid-meta">
                              {(movie.censorRating || "UA") +
                                " | " +
                                (languageLabel || "Language TBA")}
                            </span>
                          </div>
                        </article>
                      </button>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="no-results"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              className="district-no-results"
            >
              <FaSearch size={34} />
              <h3>No movies found</h3>
              <p>
                Try changing your search or filters to discover more titles.
              </p>
              <button type="button" onClick={clearFilters}>
                Reset filters
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </section>
    </div>
  );
};

export default Movies;
